import { useMutation } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Block, BlockTitle, Button, Sheet, SkeletonBlock } from "framework7-react";

import { ActivityLauncherButton } from "@/components/ActivityLauncherButton";
import { SquareBlock } from "@/components/SquareBlock";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import {
  ActivityFragmentDoc,
  CreateNextActivityByActivityIdDocument,
  EntryFragmentDoc,
  GetCandidateActivitiesDocument,
} from "@/generated/graphql/graphql";
import { useGetEntryInput } from "@/hooks/useGetEntryInput";
import { useSheetPageReady } from "@/hooks/useSheetPageReady";

const CELLS: null[] = new Array(6).fill(null);

export const ActivityLauncherSheetPage = createPage("ActivityLauncherSheetPage", PageComponent);

function PageComponent({ pageName, f7router }: PageComponentProps) {
  const { getPageProps, swipeHandler, isPageReady } = useSheetPageReady(pageName);

  const { data, loading } = useQuery(GetCandidateActivitiesDocument, {
    fetchPolicy: "cache-and-network",
    skip: !isPageReady,
  });
  const candidateActivities = getFragmentData(ActivityFragmentDoc, data?.candidateActivities);

  const getEntryInput = useGetEntryInput();
  const [createNextActivity, { loading: loadingCreateActivity }] = useMutation(CreateNextActivityByActivityIdDocument);
  return (
    <Sheet
      data-component={pageName}
      {...getPageProps()}
    >
      {swipeHandler}

      <BlockTitle
        large
        className="no-margin-top"
      >
        何をしていますか？
      </BlockTitle>
      <Block className="grid grid-cols-3 grid-gap">
        {CELLS.map((_, index) => {
          const activity = candidateActivities?.[index];

          if (loading) {
            return (
              <SquareBlock key={index}>
                <SkeletonBlock
                  borderRadius="var(--f7-button-border-radius)"
                  effect="fade"
                  height="100%"
                  tag="div"
                  width="100%"
                />
              </SquareBlock>
            );
          }

          if (activity) {
            return (
              <SquareBlock key={index}>
                <ActivityLauncherButton
                  activity={activity}
                  disabled={loadingCreateActivity}
                  onClick={async () => {
                    const entryInput = await getEntryInput();
                    await createNextActivity({
                      onCompleted: (data) => {
                        if (data.createEntryByActivityId?.entry) {
                          const entry = getFragmentData(EntryFragmentDoc, data.createEntryByActivityId.entry);
                          f7router.back();
                          f7router.navigate(`/modals/entries/${entry.id}/update-feeling/`);
                        }
                      },
                      variables: {
                        activityId: activity.id,
                        entryInput,
                      },
                    });
                  }}
                />
              </SquareBlock>
            );
          }

          return <SquareBlock key={index} />;
        })}
      </Block>
      <Block>
        <Button
          large
          tonal
          disabled={loadingCreateActivity}
          onClick={() => {
            f7router.back();
            f7router.navigate("/modals/activity-search/");
          }}
        >
          もっと見る
        </Button>
      </Block>
    </Sheet>
  );
}
